import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Banner from '../../composants/Banner/Banner';
import Button from '../../composants/Button/Button';
import ArticlesService, { Article } from '../../services/articlesService';
import { calculateTotalHT } from '../../utils/priceUtils';
import { formatPrice } from '../../utils/entityUtils';
import { handlePrint } from '../../utils/commonHandlers';
import chiffrageService from '../../services/chiffrageService';
import ChiffragePDF, { ChiffragePDFArticle } from '../PDF/ChiffragePDF';
// Le catalogue du chiffrage EST celui du bon de commande, en MODALE : meme composant, pas une copie.
import ModalArticles from '../../composants/ModalArticles/ModalArticles';
// Feuille de style de la table d'articles du BC. Importee ici volontairement : la page reutilise ses
// classes (`.articles-table-*`) pour que les deux tables restent identiques par construction, plutot
// que de recopier ses regles et de les laisser diverger.
import '../../composants/OperationConsultationBC/OperationConsultationBC.css';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { ReactComponent as CopyIcon } from '../../assets/icons/duplicate.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';
import ChiffrageEnCoursSelect from './components/ChiffrageEnCoursSelect';
import ChiffrageCreateModal from './components/ChiffrageCreateModal';
import ChiffrageSkeleton from './components/ChiffrageSkeleton';
import type { Chiffrage as ChiffrageModel, ChiffrageCouple, ChiffrageStatut } from './chiffrageTypes';
import './Chiffrage.css';

// Page "Chiffrage" (nav prestataire). Composition d'un devis pre-etabli, resolu ensuite par code cote
// acheteur. Deux modes, pilotes par l'URL :
//  - CREATION (defaut) : le presta ouvre une MODALE (nom de l'operation + couple lot technique x lot
//    geographique ou il est titulaire), le catalogue de son rang se charge INLINE (pleine page), il
//    saisit les quantites, puis Enregistre (brouillon) ou Genere le code (chiffrage immuable).
//  - AFFICHAGE (?id=<id>) : relecture d'un chiffrage existant. Lecture seule des que le code est genere,
//    sinon editable (brouillon repris).
// L'etat du chiffrage se deduit du code (pas de code = en cours, code genere = immuable) : aucun libelle
// de statut n'est affiche. Les prix ne sont jamais stockes : ils sont resolus a la volee via le
// catalogue (articlesService).

// Nom du prestataire pour l'entete du devis PDF (l'entete est optionnelle cote PDF). On le lit du
// localStorage (meme source que le reste du front : name pose a la connexion).
const getUserName = (): string | undefined => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === 'string' && parsed.name.trim() ? parsed.name : undefined;
  } catch {
    return undefined;
  }
};

// Dernier chiffrage consulte, retenu d'une visite a l'autre : y revenir vaut mieux que de rouvrir une
// page vide. L'id est TOUJOURS revalide contre la liste du presta avant d'etre suivi — un chiffrage
// supprime, masque, ou appartenant a un autre compte sur le meme navigateur ne doit pas ouvrir un
// ecran mort.
const LAST_SEEN_KEY = 'chiffrage:last-seen-id';

const readLastSeenId = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null; // stockage indisponible (navigation privee, quota) : on repartira du plus recent
  }
};

const writeLastSeenId = (id: number): void => {
  try {
    localStorage.setItem(LAST_SEEN_KEY, String(id));
  } catch {
    /* Sans stockage, on perd juste la reprise : ce n'est pas une raison d'interrompre l'affichage. */
  }
};

const messageErreur = (e: unknown): string =>
  e instanceof Error ? e.message : 'Une erreur est survenue, veuillez réessayer.';

interface Feedback {
  type: 'success' | 'danger';
  message: string;
}

const Chiffrage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const chiffrageIdFromUrl = idParam ? Number(idParam) : null;

  // Couples titulaires du prestataire connecte : alimentent les selects inline + la modale de creation,
  // et resolvent
  // l'id_lot_contrat (donc le catalogue) en affichage.
  const [couples, setCouples] = useState<ChiffrageCouple[]>([]);
  const [couplesLoaded, setCouplesLoaded] = useState(false);
  const [couplesError, setCouplesError] = useState<string | null>(null);

  // Couple retenu du chiffrage courant : id_lot_technique et id_departement, en chaine (poses par la
  // modale en creation, ou par le chargement en affichage).
  const [tech, setTech] = useState('');
  const [geo, setGeo] = useState('');

  // Chiffrage courant (persiste). `chiffrageId` non nul => couple fige : un chiffrage est lie a son lot
  // des sa creation (updateChiffrage ne touche que libelle + articles).
  const [chiffrageId, setChiffrageId] = useState<number | null>(chiffrageIdFromUrl);
  // Statut conserve pour le back (payloads / PDF). L'UI ne l'affiche pas : l'etat vient du code.
  const [statut, setStatut] = useState<ChiffrageStatut>('brouillon');
  const [code, setCode] = useState<string | null>(null);
  const [libelle, setLibelle] = useState('');

  // Catalogue INLINE du couple choisi (articles + prix du rang du presta), possede par la page.
  // Selection courante, HYDRATEE (nom, unite, prix) : c'est elle que la page affiche, que la modale
  // rejoue a l'ouverture, et que persist() renvoie au back. Plus de miroir "savedArticles" : deux
  // sources pour la meme selection, c'etait deux verites a tenir synchrones.
  const [articles, setArticles] = useState<Article[]>([]);
  // Accuse de reception de la copie du code (2s), comme le menu de reprise et la liste.
  // Accuse de reception de la copie du code (2s), comme le menu de reprise et la liste.
  const [codeCopied, setCodeCopied] = useState(false);
  // Suppression : double confirmation par modale (meme composant que la liste des chiffrages).
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  // Ligne dont le retrait est en cours de persistance : sa croix devient un loader (pattern BC).
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // Garde-fou : le chargement d'affichage (?id=) ne doit s'executer qu'une fois, sans etre ecrase par
  // l'effet de creation (qui reloaderait un catalogue vierge et effacerait les quantites enregistrees).
  const [displayLoaded, setDisplayLoaded] = useState(false);
  // Garde d'execution du chargement d'affichage. IMPERATIF : une REF, pas un state. Un state serait
  // dans les deps de l'effet et, pose depuis l'effet lui-meme, il declencherait le cleanup (annule =
  // true) qui avorte le chargement en vol -> le catalogue ne se chargeait jamais. On memorise l'id
  // charge : changer de ?id= relance naturellement, sans dependre d'un reset.
  const loadedIdRef = useRef<number | null>(null);
  // Prix totaux du menu de reprise, par id de chiffrage (resolus a la volee : aucun prix stocke).
  const [enCoursTotals, setEnCoursTotals] = useState<Map<number, number>>(new Map());
  // Miroir des couples : le calcul des totaux et le chargement des couples sont deux appels
  // paralleles, l'ordre d'arrivee n'est pas garanti -> on lit la valeur courante, pas une capture.
  const couplesRef = useRef<ChiffrageCouple[]>([]);

  // Catalogues deja resolus, par id_lot_contrat. Vit hors du cycle de rendu : les prix d'un couple
  // ne changent pas le temps d'une session, inutile de les redemander a chaque recalcul des totaux.
  const catalogueCacheRef = useRef<Map<number, Article[]>>(new Map());


  // Modale de creation (ouverte depuis le bouton "Nouveau chiffrage"). Par defaut la page affiche
  // directement les selects + le libelle : la modale ne s'ouvre plus d'office.
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Le catalogue n'est plus pose dans la page : c'est une MODALE, comme cote acheteur BC. La page
  // n'affiche que le resultat — la liste des articles retenus — pour qu'un coup d'oeil suffise a voir
  // ce qui a ete selectionne, sans devoir parcourir tout le catalogue.
  const [catalogueModalOpen, setCatalogueModalOpen] = useState(false);

  // Enchainement creation -> catalogue : la modale de creation navigue vers ?id=, ce qui rejoue tout le
  // chargement. Une REF et non un state : `resetChiffrageState()` passe entre les deux et remettrait un
  // state a zero, alors que l'intention de l'utilisateur, elle, doit survivre au rechargement.
  const openCatalogueAfterLoadRef = useRef(false);

  // Chiffrages "en cours" (sans code) du prestataire, pour le menu de reprise de la barre du haut.
  const [enCours, setEnCours] = useState<ChiffrageModel[]>([]);
  // Drapeau distinct de `enCours.length` : une liste vide chargee et une liste pas encore arrivee sont
  // deux choses differentes, et l'affichage doit attendre la seconde avant de conclure.
  const [chiffragesLoaded, setChiffragesLoaded] = useState(false);

  // Miroir de `enCours` pour l'effet de rafraichissement : il doit savoir si le chiffrage courant est
  // deja liste SANS avoir la liste dans ses deps (elle s'y rappellerait elle-meme sans fin).
  const enCoursRef = useRef<ChiffrageModel[]>([]);

  // Seul point de mutation de la liste : l'etat ET son miroir bougent ensemble. Les desynchroniser
  // ferait mentir la garde de l'effet, qui redemanderait la liste au serveur pour rien.
  const updateChiffrageList = useCallback((maj: (c: ChiffrageModel) => ChiffrageModel): ChiffrageModel[] => {
    const liste = enCoursRef.current.map(maj);
    enCoursRef.current = liste;
    setEnCours(liste);
    return liste;
  }, []);

  const isDisplayMode = chiffrageIdFromUrl != null;
  const readOnly = code != null; // code emis => chiffrage immuable

  // Couple resolu par la selection courante (tech + geo). Un presta est titulaire au plus une fois par
  // couple, donc au plus un couple correspond.
  const selectedCouple = useMemo<ChiffrageCouple | null>(
    () =>
      couples.find(
        (c) => String(c.id_lot_technique) === tech && String(c.id_departement) === geo,
      ) ?? null,
    [couples, tech, geo],
  );

  // Plus d'options de selects ici : le couple se choisit a la creation (modale), qui porte sa propre
  // liste. `tech`/`geo` restent alimentes par le chargement d'un chiffrage — ils resolvent le couple.

  // Requete catalogue passee a ModalArticles. MEMOISEE : son effet de chargement a `commandes` dans
  // ses deps -> une nouvelle reference a chaque rendu le relancerait en boucle (meme piege que
  // CatalogueLotModal). `id_contrat` = le pivot lot_contrat, c'est lui qui resout les prix du rang.
  const commandesCatalogue = useMemo(
    () =>
      selectedCouple
        ? [
            {
              id_contrat: selectedCouple.id_lot_contrat,
              catalogue_prix_id_lot_contrat: selectedCouple.id_lot_contrat,
              id_lot_departement: selectedCouple.id_departement,
              id_lot_technique: selectedCouple.id_lot_technique,
            },
          ]
        : [],
    [selectedCouple],
  );

  // Selection rejouee a l'ouverture du catalogue : la selection COURANTE (`articles`), pas la derniere
  // enregistree. Rouvrir le catalogue apres avoir ajoute puis supprime des lignes doit montrer ce qui
  // est a l'ecran, pas ce qui dort en base. `articles` est un state -> reference stable entre deux
  // rendus, ce qui suffit a l'effet de chargement de ModalArticles (piege des refs recreees).
  const existingSelection = articles;

  // 1) Couples titulaires du prestataire.
  useEffect(() => {
    let annule = false;
    chiffrageService
      .getCouples()
      .then((data) => {
        if (annule) return;
        // couples ET couplesLoaded poses ENSEMBLE : l'effet d'affichage se declenche sur couplesLoaded
        // et lit couples -> les separer (.finally) laissait la porte a un tour ou la liste est encore
        // vide, donc a un couple introuvable ("plus titulaire") alors qu'il existe.
        setCouples(data);
        couplesRef.current = data;
        setCouplesLoaded(true);
        // Les couples peuvent arriver APRES la liste : on (re)calcule alors les totaux, sinon le menu
        // resterait sans prix.
        if (enCours.length > 0) computeEnCoursTotals(enCours);
      })
      .catch((e) => {
        if (annule) return;
        setCouplesError(messageErreur(e));
        setCouplesLoaded(true);
      });
    return () => {
      annule = true;
    };
  }, []);

  // 1bis) Menu de reprise : TOUS les chiffrages du presta, code genere ou non. Seuls les chiffrages
  // supprimes de sa liste (masque_at) sont exclus — ils n'ont plus a lui etre proposes, meme si leur
  // code reste valide cote acheteur. Liste d'appoint : un echec ne bloque pas la page.
  useEffect(() => {
    // Le refetch ne sert qu'a faire ENTRER dans la liste un chiffrage qu'elle ignore (une creation).
    // Basculer entre deux chiffrages deja listes ne change rien a la liste : la redemander a chaque
    // bascule etait un appel pour rien. On lit la liste via une ref, sinon elle serait dans les deps
    // et l'effet se rappellerait lui-meme a l'infini.
    if (chiffrageId != null && enCoursRef.current.some((c) => c.id === chiffrageId)) return undefined;

    let annule = false;
    chiffrageService
      .listChiffrages()
      .then((data) => {
        if (annule) return;
        const liste = data.filter((c) => !c.masque_at);
        setEnCours(liste);
        enCoursRef.current = liste;
        setChiffragesLoaded(true);
        // Prix totaux du menu : resolus a la volee (aucun prix stocke), catalogue mis en cache par
        // id_lot_contrat -> quelques appels (un par couple), pas un par chiffrage.
        computeEnCoursTotals(liste);
      })
      .catch(() => {
        // Silencieux : la reprise reste optionnelle. Mais on debloque l'affichage, qui retombera sur
        // l'appel de detail — sinon un echec ici figerait la page sur son skeleton.
        if (!annule) setChiffragesLoaded(true);
      });
    return () => {
      annule = true;
    };
    // `computeEnCoursTotals` est volontairement hors des deps : il est declare plus bas, donc l'y
    // mettre le lirait avant son initialisation (TDZ) — et il est stable (useCallback sans deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiffrageId]);

  // Prix total de chaque chiffrage du menu de reprise. Aucun prix n'est stocke : on resout le
  // catalogue du titulaire, MIS EN CACHE par id_lot_contrat (un presta a peu de couples). Les couples
  // sont relus depuis la ref pour ne pas dependre de l'ordre de chargement des deux listes.
  const computeEnCoursTotals = useCallback(async (liste: ChiffrageModel[]) => {
    const couplesCourants = couplesRef.current;
    if (couplesCourants.length === 0) return;

    const byKey = new Map<string, ChiffrageCouple>();
    couplesCourants.forEach((c) =>
      byKey.set(`${Number(c.id_lot_technique)}:${Number(c.id_departement)}`, c),
    );

    // Cache PERSISTANT (ref) et non local a l'appel : cet effet rejoue a chaque changement de
    // chiffrage courant, et un cache local repartait de zero a chaque fois -> N appels catalogue
    // rien que pour rebasculer d'un chiffrage a l'autre. Les prix d'un lot_contrat ne bougent pas
    // le temps d'une session : on ne les redemande donc qu'une fois par couple.
    const catalogueCache = catalogueCacheRef.current;
    const totals = new Map<number, number>();

    for (const ch of liste) {
      const lignes = ch.articles || [];
      const couple = byKey.get(`${Number(ch.id_lot_technique)}:${Number(ch.id_departement)}`);
      if (!couple || lignes.length === 0) {
        totals.set(ch.id, 0);
        continue;
      }
      let catalogue = catalogueCache.get(couple.id_lot_contrat);
      if (!catalogue) {
        catalogue = await ArticlesService.getArticlesByCommandes([
          {
            id_contrat: couple.id_lot_contrat,
            id_lot_departement: ch.id_departement,
            id_lot_technique: ch.id_lot_technique,
          },
        ]).catch(() => [] as Article[]);
        catalogueCache.set(couple.id_lot_contrat, catalogue);
      }
      const byId = new Map<string, Article>();
      catalogue.forEach((a) => byId.set(String(a.id), a));

      totals.set(
        ch.id,
        lignes.reduce((sum, ligne) => {
          const art = byId.get(String(ligne.id_catalogue));
          const pu = art ? Number(ArticlesService.getPrixByQuantity(art, ligne.quantite)) || 0 : 0;
          return sum + pu * ligne.quantite;
        }, 0),
      );
    }
    setEnCoursTotals(totals);
  }, []);

  // Catalogue d'un couple, via le cache partage avec les totaux du menu de reprise : la page et le
  // menu demandent le meme catalogue, autant ne le payer qu'une fois.
  const resolveCatalogue = useCallback(async (couple: ChiffrageCouple): Promise<Article[]> => {
    const cache = catalogueCacheRef.current;
    const cached = cache.get(couple.id_lot_contrat);
    if (cached) return cached;

    const catalogue = await ArticlesService.getArticlesByCommandes([
      {
        id_contrat: couple.id_lot_contrat,
        catalogue_prix_id_lot_contrat: couple.id_lot_contrat,
        id_lot_departement: couple.id_departement,
        id_lot_technique: couple.id_lot_technique,
      },
    ]).catch(() => [] as Article[]);
    cache.set(couple.id_lot_contrat, catalogue);
    return catalogue;
  }, []);

  // Les lignes enregistrees ne portent que {id_catalogue, quantite} : aucun prix n'est stocke en base.
  // La page doit pourtant afficher nom / unite / prix DES le chargement, sans attendre que l'utilisateur
  // ouvre le catalogue. On les rehydrate donc ici depuis le catalogue du couple.
  const hydrateArticles = useCallback(
    async (couple: ChiffrageCouple, lignes: { id_catalogue: number; quantite: number }[]): Promise<Article[]> => {
      if (lignes.length === 0) return [];
      const catalogue = await resolveCatalogue(couple);
      const byId = new Map<string, Article>();
      catalogue.forEach((a) => byId.set(String(a.id), a));

      return lignes
        .map((ligne) => {
          const art = byId.get(String(ligne.id_catalogue));
          if (!art) return null; // article retire du catalogue : on ne fabrique pas une ligne fantome
          const prixUnitaire = Number(ArticlesService.getPrixByQuantity(art, ligne.quantite)) || 0;
          return { ...art, quantite: ligne.quantite, totalHT: calculateTotalHT(prixUnitaire, ligne.quantite) };
        })
        .filter((a): a is Article => a !== null);
    },
    [resolveCatalogue],
  );

  // 1ter) Arrivee sans ?id= : on rouvre le dernier chiffrage consulte plutot qu'une page vide. A defaut
  // (premiere visite, id devenu invalide), le plus recent de la liste — elle est triee par id
  // decroissant cote bdc. Aucun chiffrage : on laisse le message d'invite, il n'y a rien a ouvrir.
  //
  // `replace: true` : cette redirection n'est pas une navigation de l'utilisateur, elle ne doit pas
  // s'empiler dans l'historique — sinon "Precedent" le ramenerait ici pour le rediriger a nouveau.
  // Pas de garde "une seule fois" : elle bloquerait le cas le plus courant. Recliquer "Chiffrage" dans
  // le menu depuis /chiffrage?id=4 ne REMONTE pas le composant — une garde par ref serait deja
  // consommee et laisserait la page vide, exactement ce qu'on corrige. L'effet se coupe seul : rediriger
  // pose ?id=, donc `isDisplayMode` passe a true au tour suivant.
  useEffect(() => {
    if (isDisplayMode || !chiffragesLoaded) return;

    const liste = enCoursRef.current;
    if (liste.length === 0) return;

    const lastSeen = readLastSeenId();
    const cible = liste.find((c) => c.id === lastSeen) ?? liste[0];
    navigate(`/chiffrage?id=${cible.id}`, { replace: true });
  }, [isDisplayMode, chiffragesLoaded, navigate]);

  // 2) Mode AFFICHAGE : charge le chiffrage puis son catalogue (une seule fois, apres les couples).
  useEffect(() => {
    if (!isDisplayMode || chiffrageIdFromUrl == null || !couplesLoaded || !chiffragesLoaded) return;
    if (loadedIdRef.current === chiffrageIdFromUrl) return; // deja charge pour cet id
    loadedIdRef.current = chiffrageIdFromUrl;
    let annule = false;

    (async () => {
      try {
        // La liste (GET /bdc/chiffrage) porte DEJA tout ce que le detail renverrait : meme modele,
        // meme `with('articles')`, meme scope id_entite — elle ajoute meme articles_count. Appeler
        // /bdc/chiffrage/{id} par-dessus, c'etait redemander ce qu'on avait deja. On n'y retombe que
        // si le chiffrage n'est pas dans la liste : elle exclut les masques, qui restent ouvrables
        // par leur URL.
        const liste = enCoursRef.current.find((c) => c.id === chiffrageIdFromUrl);
        const chiffrage = liste ?? (await chiffrageService.getChiffrage(chiffrageIdFromUrl));
        if (annule) return;
        setChiffrageId(chiffrage.id);
        setStatut(chiffrage.statut);
        setCode(chiffrage.code);
        setLibelle(chiffrage.libelle ?? '');
        setTech(String(chiffrage.id_lot_technique));
        setGeo(String(chiffrage.id_departement));

        setDisplayLoaded(true); // drapeau de RENDU uniquement (la garde d'execution est la ref)
        // Memorise APRES un chargement reussi : retenir un id qui vient d'echouer condamnerait les
        // visites suivantes a rouvrir le meme echec.
        writeLastSeenId(chiffrage.id);

        // Comparaison normalisee : les deux sources sont typees int cote back, mais on ne fait pas
        // dependre l'affichage du catalogue d'une egalite stricte de types traversant deux APIs.
        const couple = couples.find(
          (c) =>
            Number(c.id_lot_technique) === Number(chiffrage.id_lot_technique) &&
            Number(c.id_departement) === Number(chiffrage.id_departement),
        );
        if (!couple) {
          // Presta plus titulaire sur ce couple : les prix ne peuvent plus etre resolus. Le code
          // reste valide, mais le detail chiffre n'est plus affichable ici.
          setCatalogueError(
            "Vous n'êtes plus titulaire sur ce couple lot technique et lot géographique : le détail des prix ne peut plus être affiché.",
          );
        } else {
          // Table des articles retenus : elle doit etre lisible tout de suite, pas au premier clic sur
          // le catalogue.
          const hydrated = await hydrateArticles(couple, chiffrage.articles ?? []);
          if (annule) return;
          setArticles(hydrated);

          if (openCatalogueAfterLoadRef.current && chiffrage.code == null) {
            // Suite de la creation : le couple est resolu, le catalogue est donc chargeable. Jamais sur
            // un chiffrage deja code (immuable) — meme si la ref trainait.
            setCatalogueModalOpen(true);
          }
        }
        openCatalogueAfterLoadRef.current = false;
      } catch (e) {
        if (!annule) setFeedback({ type: 'danger', message: messageErreur(e) });
      }
    })();

    return () => {
      annule = true;
    };
    // `displayLoaded` est volontairement HORS des deps : il est pose par cet effet (cf. la ref).
    // `chiffragesLoaded` gate l'effet : il lit la liste, il doit donc attendre qu'elle soit arrivee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisplayMode, chiffrageIdFromUrl, couplesLoaded, couples, chiffragesLoaded]);

  // Plus d'effet de chargement du catalogue ici : ModalArticles se recharge de lui-meme quand
  // `commandesCatalogue` change (il derive du couple courant).

  // Remise a zero de l'etat du chiffrage courant (les couples titulaires, eux, restent charges).
  // Utilise avant de repartir sur un nouveau chiffrage ou d'en reprendre un autre : sans ce reset, les
  // gardes d'effet (chiffrageId/displayLoaded) empecheraient le rechargement du catalogue.
  const resetChiffrageState = useCallback(() => {
    setChiffrageId(null);
    setCode(null);
    setStatut('brouillon');
    setLibelle('');
    setTech('');
    setGeo('');
    setArticles([]);
    setCatalogueError(null);
    setDisplayLoaded(false);
    loadedIdRef.current = null; // sinon rouvrir le meme id ne rechargerait plus (garde d'execution)
    setFeedback(null);
  }, []);

  // Bouton "Nouveau chiffrage" : repart d'une feuille vierge et ouvre la modale (un nouveau chiffrage
  // n'herite jamais de l'etat du precedent).
  // Ouvre SEULEMENT la modale : on ne touche pas au chiffrage courant tant que rien n'est valide.
  // Sinon, annuler laissait la page vide (l'etat avait deja ete efface a l'ouverture). C'est
  // `handleCreated` qui reinitialise et bascule sur le nouveau chiffrage, une fois celui-ci cree.
  const handleNewChiffrage = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  // Reprise d'un chiffrage en cours : on reinitialise puis on cible ?id= ; l'effet d'affichage rejouera
  // le chargement (displayLoaded a ete remis a false).
  const handleOpenEnCours = useCallback(
    (id: number) => {
      resetChiffrageState();
      navigate(`/chiffrage?id=${id}`);
    },
    [resetChiffrageState, navigate],
  );

  // La modale a CREE le chiffrage (persiste, sans article) : on repart d'une base vierge puis on cible
  // ?id=, l'effet d'affichage rejouera le chargement (chiffrage + son catalogue). Le catalogue s'ouvre
  // dans la foulee : creer un chiffrage vide puis devoir cliquer pour le remplir serait un clic de plus
  // pour la seule chose qu'on vient de demander.
  const handleCreated = useCallback(
    (chiffrage: ChiffrageModel) => {
      setCreateModalOpen(false);
      resetChiffrageState();
      openCatalogueAfterLoadRef.current = true;
      navigate(`/chiffrage?id=${chiffrage.id}`);
    },
    [resetChiffrageState, navigate],
  );


  // Plus de gestion des quantites ici : elle vit dans ModalArticles (le composant du BC), qui remonte
  // la selection complete via `onSelectionChange`.

  const selectedCount = articles.filter((a) => a.quantite > 0).length;

  // Met a jour le brouillon courant. Un chiffrage naît TOUJOURS de la modale de creation (c'est elle
  // qui porte le nom de l'operation) : on ne cree donc RIEN ici, sinon les selects offriraient un
  // chemin de creation sans libelle. Sans chiffrage courant, il n'y a rien a enregistrer.
  //
  // La liste est passee en ARGUMENT et jamais lue depuis le state : `setArticles` est asynchrone, donc
  // un enregistrement declenche dans la foulee d'un geste lirait `articles` dans son etat d'AVANT le
  // geste — et sauvegarderait exactement l'inverse de ce que l'utilisateur vient de faire.
  // Ne capture pas l'erreur : chaque appelant decide s'il l'affiche (autosave) ou la propage (code).
  const persist = useCallback(
    async (aPersister: Article[]): Promise<number> => {
      if (chiffrageId == null) {
        throw new Error('Veuillez passer par « Nouveau chiffrage » pour créer un chiffrage.');
      }
      const updated = await chiffrageService.updateChiffrage(chiffrageId, {
        libelle: libelle.trim() || null,
        articles: aPersister
          .filter((a) => a.quantite > 0)
          .map((a) => ({ id_catalogue: Number(a.id), quantite: a.quantite })),
      });
      setStatut(updated.statut);

      // Le menu de reprise affiche, pour CHAQUE chiffrage, son nombre d'articles et son total : deux
      // valeurs derivees des quantites qu'on vient de changer. Sans ce rafraichissement, le chiffrage
      // courant y resterait affiche avec le compte et le montant d'avant la modification.
      // Le recalcul ne coute aucun appel reseau : les prix du couple sont deja en cache (couplesRef).
      computeEnCoursTotals(updateChiffrageList((c) => (c.id === chiffrageId ? { ...c, ...updated } : c)));

      return chiffrageId;
    },
    [chiffrageId, libelle, computeEnCoursTotals, updateChiffrageList],
  );

  // Retrait d'une ligne : enregistrement automatique, calque sur le BC (handleRemoveExistingArticle).
  //
  // L'ordre compte — on persiste D'ABORD, on met a jour l'affichage APRES. L'inverse (retrait optimiste)
  // ferait disparaitre la ligne de l'ecran meme quand l'appel echoue : l'utilisateur la croirait
  // supprimee alors qu'elle est toujours en base.
  //
  // Retirer la DERNIERE ligne rouvre le catalogue : un chiffrage vide n'a rien a montrer, et la seule
  // suite possible est d'y retourner. L'ouverture est faite ICI, sur le geste, et non dans un effet sur
  // `articles.length === 0` : un tel effet rouvrirait la modale a l'instant meme ou on la ferme sans
  // rien choisir.
  const handleRemoveArticle = useCallback(
    async (id: string) => {
      const restant = articles.filter((a) => String(a.id) !== String(id));
      setDeletingId(String(id));
      setFeedback(null);
      try {
        await persist(restant);
        setArticles(restant);
        if (restant.length === 0 && !readOnly) setCatalogueModalOpen(true);
      } catch (e) {
        setFeedback({ type: 'danger', message: messageErreur(e) });
      } finally {
        setDeletingId(null);
      }
    },
    [articles, readOnly, persist],
  );

  // Plus de « Enregistrer et quitter » : la liste est persistee a chaque mutation (retrait, validation
  // du catalogue), le
  // bouton n'avait donc plus rien a enregistrer et laissait croire que quitter sans lui perdait tout.

  const handleGenerate = useCallback(async () => {
    if (selectedCount === 0) {
      setFeedback({ type: 'danger', message: 'Veuillez saisir au moins une quantité avant de générer le code.' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      // Enregistrement de securite avant de figer : l'autosave a deja passe la liste, mais s'il avait
      // echoue (reseau), generer le code figerait un chiffrage incomplet. Ici `articles` est le state
      // courant, a jour au moment du clic — pas de course avec un setState.
      const id = await persist(articles);
      const activated = await chiffrageService.generateCode(id);
      setCode(activated.code);
      setStatut(activated.statut);

      // Le menu de reprise porte le code et l'etat de CHAQUE chiffrage : sans ca, celui qu'on vient
      // d'activer y resterait affiche "Brouillon", sans code ni bouton de copie. Son effet ne rejoue
      // pas ici (il ne depend que de `chiffrageId`, que generer un code ne change pas) — et il n'a
      // pas a rejouer : la reponse porte deja le chiffrage active, on l'applique.
      // Le total affiche dans le menu vient des quantites : persist() vient peut-etre de les changer.
      // Le recalcul ne coute aucun appel — le catalogue du couple est deja dans le cache.
      computeEnCoursTotals(updateChiffrageList((c) => (c.id === id ? { ...c, ...activated } : c)));

      setFeedback({
        type: 'success',
        message: 'Le code de votre chiffrage a été généré. Communiquez-le à votre acheteur.',
      });
    } catch (e) {
      setFeedback({ type: 'danger', message: messageErreur(e) });
    } finally {
      setSaving(false);
    }
    // `articles` EST une dependance depuis que la generation persiste la liste courante : sans lui,
    // le callback figerait la liste du rendu ou il a ete cree et genererait le code sur un etat perime.
  }, [persist, articles, selectedCount, updateChiffrageList, computeEnCoursTotals]);

  // "Supprimer" : seul un brouillon est supprimable (le back le garantit aussi). Confirmation explicite
  // car l'action est definitive.
  // Suppression en DEUX temps, comme la liste : la corbeille arme la modale (aucun appel reseau),
  // la modale confirme. Meme composant que la liste (ValidationPopUp) plutot qu'un window.confirm
  // natif, pour un parcours homogene.
  const handleDelete = useCallback(() => {
    if (chiffrageId == null) return;
    setDeleteError(null);
    setDeleteModalOpen(true);
  }, [chiffrageId]);

  // Deux mecaniques selon l'existence d'un CODE (meme regle que la liste) : sans code -> suppression
  // reelle ; avec code -> le chiffrage sort de la liste du presta mais le CODE RESTE VALIDE pour les
  // acheteurs (un code communique ne se revoque pas).
  const confirmDelete = useCallback(async () => {
    if (chiffrageId == null) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (code) {
        await chiffrageService.hideChiffrage(chiffrageId);
      } else {
        await chiffrageService.deleteChiffrage(chiffrageId);
      }
      navigate('/commandes', { state: { openChiffragesTab: true } });
    } catch (e) {
      // La modale reste ouverte et porte l'erreur (on ne perd pas le contexte).
      setDeleteError(messageErreur(e));
      setIsDeleting(false);
    }
  }, [chiffrageId, code, navigate]);

  // Meme accuse de reception que le menu de reprise et la liste : icone -> check + "Copié" pendant 2s,
  // sur le SEUL bouton clique (d'ou le `place`).
  const handleCopy = useCallback(async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      window.setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      setFeedback({ type: 'danger', message: 'La copie automatique a échoué, veuillez copier le code manuellement.' });
    }
  }, [code]);

  const handleDownloadPdf = useCallback(async () => {
    if (!code || !selectedCouple) return;
    const selected = articles.filter((a) => a.quantite > 0);
    const pdfArticles: ChiffragePDFArticle[] = selected.map((a) => ({
      code: a.code,
      nom: a.nom,
      quantite: a.quantite,
      prixUnitaire: ArticlesService.getPrixByQuantity(a, a.quantite),
      prixTotal: a.totalHT,
      unite: a.unite,
    }));
    const total = pdfArticles.reduce((sum, a) => sum + a.prixTotal, 0);

    const chiffragePourPdf: ChiffrageModel = {
      id: chiffrageId ?? 0,
      id_lot_technique: selectedCouple.id_lot_technique,
      id_departement: selectedCouple.id_departement,
      rang: selectedCouple.rang,
      libelle: libelle.trim() || null,
      statut,
      code,
      code_genere_at: null,
      masque_at: null,
      expire_at: null,
    };

    await handlePrint(
      <ChiffragePDF
        chiffrage={chiffragePourPdf}
        articles={pdfArticles}
        entete={{ nom: getUserName() }}
        total={total}
        code={code}
      />,
      `chiffrage-${code}.pdf`,
    );
  }, [code, selectedCouple, articles, chiffrageId, libelle, statut]);

  const total = useMemo(
    () =>
      parseFloat(
        articles
          .filter((a) => a.quantite > 0)
          .reduce((sum, a) => sum + a.totalHT, 0)
          .toFixed(4),
      ),
    [articles],
  );

  // Bouton de copie du code, en pied de page. L'icone passe par `value` et non par le prop `icon` :
  // le composant Button rend toujours l'icone AVANT le label, or on la veut a droite (comme le menu
  // de reprise).
  const codeButton = code ? (
    <Button
      variant="primary"
      className="chiffrage-code-btn"
      title={codeCopied ? 'Code copié' : 'Copier le code'}
      onClick={handleCopy}
      value={
        <>
          <span className="chiffrage-code-btn-value">{code}</span>
          {codeCopied ? (
            <CheckIcon className="chiffrage-code-btn-icon chiffrage-code-btn-icon--check" />
          ) : (
            <CopyIcon className="chiffrage-code-btn-icon" />
          )}
          {codeCopied && <span className="chiffrage-code-btn-copie">Copié</span>}
        </>
      }
    />
  ) : null;

  // Premier rendu : tant que les couples ne sont pas la, la page n'a ni ses selects ni son catalogue.
  // On decalque sa mise en page plutot que d'afficher un vide qui sautera au chargement. L'erreur de
  // chargement, elle, doit passer : sinon le skeleton tournerait indefiniment sur un echec.
  // `chiffragesLoaded` compte ici aussi : l'affichage d'un chiffrage sert desormais depuis la liste, donc
  // la page n'a pas son contenu tant qu'elle n'est pas la.
  // `!chiffragesLoaded` compte dans TOUS les cas, plus seulement en affichage : sans ?id=, la page
  // attend la liste pour savoir vers quel chiffrage rebondir. Rendre l'invite entre-temps ferait
  // clignoter la page vide qu'on cherche precisement a ne plus montrer.
  if ((!couplesLoaded || !chiffragesLoaded) && !couplesError) {
    return (
      <div className="chiffrage-page">
        <ChiffrageSkeleton />
      </div>
    );
  }

  return (
    <div className="chiffrage-page">
      {/* Banniere d'information conservee (contexte de la feature cote prestataire). */}
      <Banner
        variant="info"
        message="Cet onglet Chiffrages vous est mis à disposition afin que vous puissiez réaliser des chiffrages pour les bons de commande, sur la base de vos prix en vigueur, sur demande des acheteurs. Dès que vous aurez cliqué sur « Générer le code », un code apparaitra et il vous appartiendra de le communiquer à votre acheteur afin qu'il récupère votre chiffrage. Une fois le code généré, vous ne pouvez plus modifier le chiffrage."
      />

      {feedback && (
        <Banner variant={feedback.type} filled className="chiffrage-feedback" message={feedback.message} />
      )}

      {/* Plus de selects de lot : le couple est choisi a la CREATION (modale), et il est fige ensuite.
          Les garder ici, verrouilles, c'etait afficher un choix qui n'en est plus un. */}
      <div className="chiffrage-toolbar">
          {/* Pas de champ libelle ici : le nom de l'operation est saisi a la creation (modale). L'etat
              `libelle` reste charge et renvoye par persist() -> aucune perte a l'enregistrement. */}
          <Button value="Nouveau chiffrage" variant="primary" onClick={handleNewChiffrage} />
          {/* Menu de reprise : rendu custom (deux lignes par entree), impossible avec un <option>.
              Il porte le chiffrage courant et n'offre aucune deselection. */}
          <ChiffrageEnCoursSelect
            chiffrages={enCours}
            couples={couples}
            totals={enCoursTotals}
            currentId={chiffrageId}
            onSelect={handleOpenEnCours}
          />
          <Button value="Tous les chiffrages" variant="secondary" onClick={() => navigate('/commandes', { state: { openChiffragesTab: true } })} />
      </div>

      {/* Plus de bouton de code en haut : le menu de reprise porte deja le code du chiffrage courant,
          juste au-dessus. Deux fois la meme information a deux lignes d'ecart, c'etait du bruit. */}

      {couplesError && (
        <Banner variant="danger" className="chiffrage-feedback" message={couplesError} />
      )}

      {couplesLoaded && !couplesError && couples.length === 0 && (
        <Banner
          variant="info"
          className="chiffrage-feedback"
          message="Vous n'êtes titulaire sur aucun couple lot technique et lot géographique : la création d'un chiffrage n'est pas disponible pour le moment."
        />
      )}

      {/* La page n'affiche plus le catalogue, mais son RESULTAT : la liste des articles retenus, comme
          cote acheteur BC. Memes classes que la table du BC (`.articles-table-*`), et sa feuille de
          style importee explicitement : une table repliquee ici finirait par diverger de l'originale —
          c'est exactement ce qu'on a deja paye avec l'ancien ChiffrageArticleList. */}
      {(selectedCouple || (isDisplayMode && displayLoaded)) ? (
        <>
          {/* Plus de rappel des lots ici : le menu de reprise porte deja la ligne complete du chiffrage
              courant, et le titre de la modale porte le couple. Le repeter une troisieme fois etait du
              bruit. */}
          <div className="articles-table-container">
            {catalogueError && (
              <Banner variant="danger" className="chiffrage-feedback" message={catalogueError} />
            )}

            {/* Le bouton d'ajout vit DANS le wrapper, comme cote acheteur BC : c'est ce parent qui
                porte son rendu (les regles du BC l'exigent) et qui l'integre visuellement au tableau.
                Le wrapper est donc rendu meme sans article — sinon la seule porte vers le catalogue
                disparaitrait justement quand le chiffrage est vide. */}
            <div className="articles-table-wrapper">
              {articles.length === 0 ? (
                <p className="chiffrage-placeholder">Aucun article sélectionné pour le moment.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>N° Réf</th>
                      <th>Description de la prestation</th>
                      <th>Unité</th>
                      <th>Quantité</th>
                      <th>Prix unitaire HT</th>
                      <th>Total HT</th>
                      {!readOnly && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((article) => (
                      <tr key={article.id}>
                        <td className="article-number">{article.code}</td>
                        <td className="article-description">
                          <div className="article-name">{article.nom}</div>
                        </td>
                        <td className="article-unit">{article.unite}</td>
                        <td className="article-quantity">
                          <div className="quantity-cell">{article.quantite}</div>
                        </td>
                        <td className="article-price price-cell">
                          {formatPrice(Number(ArticlesService.getPrixByQuantity(article, article.quantite)) || 0, 4, true)}
                        </td>
                        <td className="article-total total-cell">{formatPrice(article.totalHT, 4, true)}</td>
                        {!readOnly && (
                          <td className="article-actions">
                            {/* Pendant l'enregistrement, la croix cede la place a un loader circulaire
                                sur LA ligne concernee — meme rendu que le BC (OperationConsultation).
                                Les autres croix sont desactivees : deux suppressions concurrentes
                                enverraient deux listes calculees sur le meme etat de depart. */}
                            <button
                              className="delete-button"
                              type="button"
                              title="Retirer cet article"
                              onClick={() => handleRemoveArticle(article.id)}
                              disabled={deletingId !== null}
                            >
                              {deletingId === String(article.id) ? (
                                <span className="delete-spinner" aria-label="Enregistrement en cours" />
                              ) : (
                                '×'
                              )}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Seule porte d'entree du catalogue avec la creation : il n'est plus dans la page. */}
              {!readOnly && !catalogueError && (
                <button
                  className="add-article-button"
                  type="button"
                  onClick={() => setCatalogueModalOpen(true)}
                  disabled={!selectedCouple}
                >
                  Ajouter ou modifier un article
                </button>
              )}
            </div>

            <div className="articles-actions">
              <div className="total-section">
                <p>Total HT : {formatPrice(total, 4, true)}</p>
              </div>
            </div>

            <div className="chiffrage-actions">
              {codeButton}
              {/* Disponible AUSSI quand le code est genere (le chiffrage est fige, mais il reste
                  supprimable de la liste du presta). */}
              {chiffrageId != null && (
                <Button
                  value="Supprimer"
                  variant="cancel"
                  onClick={handleDelete}
                  disabled={saving || deletingId !== null}
                />
              )}
              {/* Le devis n'a de sens qu'une fois le code genere : le bouton n'existe pas avant. */}
              {code && (
                <Button
                  value="Télécharger en PDF"
                  variant="secondary"
                  onClick={handleDownloadPdf}
                  disabled={!selectedCouple}
                />
              )}
              {/* Aucun indicateur global ici : l'enregistrement se signale LA ou il se produit — loader
                  circulaire a la place de la croix pour un retrait, « Sauvegarde... » dans la modale
                  pour un ajout. Meme parti-pris que le BC. */}
              {!readOnly && (
                // La generation du code, elle, exige un chiffrage non vide (regle metier).
                // Bloquee pendant un retrait en cours : elle repersiste `articles`, donc l'etat
                // d'AVANT le retrait — elle reecrirait la ligne qu'on est en train de supprimer.
                <Button
                  value={saving ? 'Traitement...' : 'Générer le code'}
                  variant="primary"
                  onClick={handleGenerate}
                  disabled={saving || deletingId !== null || chiffrageId == null || selectedCount === 0}
                />
              )}
            </div>
          </div>

          {/* LE catalogue du BC, en MODALE : meme composant que celui de l'acheteur, pas une copie.
              `onValidateExternal` est IMPERATIF ici : sans lui, la modale persiste elle-meme sa
              selection en POST sur /bdc/operation/{id}/catalogue, avec un id devine depuis l'URL. Sur
              /chiffrage il n'y a pas d'operation — elle echouait sur "ID de l'entite non trouve". Ce
              prop existe pour ce cas exact : le parent possede la persistance (ici : persist(), au
              moment de l'enregistrement ou de la generation du code). */}
          {catalogueModalOpen && (
            <ModalArticles
              isOpen
              readOnly={readOnly}
              commandes={commandesCatalogue}
              existingSelection={existingSelection}
              prestataireRang={selectedCouple?.rang ?? 1}
              // Titre = le couple entier (lot technique PUIS lot geographique) : c'est lui qui situe le
              // catalogue qu'on parcourt, et il remplace le rappel qui vivait au-dessus de la table.
              lotNom={
                selectedCouple
                  ? `${selectedCouple.lot_code} - ${selectedCouple.lot_nom} - ${selectedCouple.id_departement} - ${selectedCouple.departement_nom}`
                  : ''
              }
              emptyMessage={catalogueError ?? undefined}
              // Enregistrement automatique a la validation : la modale porte AUSSI les quantites, donc
              // ce chemin couvre l'ajout ET la modification. On lui REND la promesse — elle l'attend
              // (cf. ModalArticles), affiche son propre « Sauvegarde... » pendant l'appel, et si la
              // persistance echoue elle reste ouverte avec l'erreur : la selection n'est pas perdue.
              // D'ou l'absence de try/catch ici, volontaire.
              onValidateExternal={async (selection) => {
                await persist(selection);
                setArticles(selection);
                setCatalogueModalOpen(false);
              }}
              onAddToCommand={() => {}}
              onClose={() => setCatalogueModalOpen(false)}
            />
          )}
        </>
      ) : (
        couplesLoaded &&
        couples.length > 0 &&
        !isDisplayMode && (
          <p className="chiffrage-placeholder">
            Cliquez sur « Nouveau chiffrage » pour composer votre chiffrage sur la base de vos prix en
            vigueur.
          </p>
        )
      )}

      {/* Double confirmation de suppression — MEME composant et meme formulation que la liste des
          chiffrages. Le texte dit la verite : un code deja communique reste utilisable. */}
      <ValidationPopUp
        isOpen={deleteModalOpen}
        title="Supprimer le chiffrage"
        text={
          deleteError ||
          (code
            ? `Êtes-vous sûr de vouloir supprimer ce chiffrage${
                libelle.trim() ? ` « ${libelle.trim()} »` : ''
              } ? Il sera retiré de votre liste. Le code ${code} que vous avez communiqué reste valide : les acheteurs peuvent continuer à l'utiliser.`
            : `Êtes-vous sûr de vouloir supprimer ce chiffrage${
                libelle.trim() ? ` « ${libelle.trim()} »` : ''
              } ? Cette action est irréversible.`)
        }
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteError(null);
          setIsDeleting(false);
        }}
        onConfirm={confirmDelete}
        cancelLabel="Annuler"
        confirmLabel="Supprimer"
        isLoading={isDeleting}
      />

      {/* Modale de creation (nom de l'operation + couple titulaire + vignette coordonnees). */}
      <ChiffrageCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        couples={couples}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default Chiffrage;
